import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FEATURE_BY_ROUTE } from '../data/features';

const CONTACT = 'contact@lodonu.dev';

// One component serves every not-yet-built tool route. It looks the current
// feature up from the path and renders a tasteful "in development" panel.
function ComingSoon() {
  const { pathname } = useLocation();
  const feature = FEATURE_BY_ROUTE[pathname];

  if (!feature) return null;
  const Preview = PREVIEWS[feature.id] || PreviewGeneric;

  return (
    <>
      <Helmet>
        <title>{feature.name} · VantageLabs</title>
        <meta name="description" content={feature.about} />
        <link rel="canonical" href={`https://vantage.lodonu.dev${feature.route}`} />
      </Helmet>

      <div className="ws-pane-head">
        <span className="ws-pane-title">{feature.name}</span>
        <span className="ws-crumbs">/ {feature.kind} · in development</span>
        <span className="ws-pane-spacer" />
        <span className="mono ws-soon-eta">
          <span className="ws-soon-pip" aria-hidden="true" />
          coming · {feature.eta || 'soon'}
        </span>
      </div>

      <div className="ws-pane-body">
        <div className="ws-soon-card">
          <div className="ws-soon-meta">
            <div className="mono ws-muted">
              Feature {feature.n} · {feature.kind}
            </div>
            <h2 className="ws-soon-headline">{feature.headline}</h2>
            <p className="ws-soon-about">{feature.about}</p>

            <div className="ws-soon-caps">
              <div className="mono ws-soon-caps-head">Planned capabilities</div>
              <ul>
                {feature.bullets.map((b) => (
                  <li key={b}>
                    <span className="mono ws-muted">—</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="ws-soon-actions">
              <button className="ws-btn" type="button" disabled>
                Open tool · disabled
              </button>
              <a
                className="ws-btn"
                href={`mailto:${CONTACT}?subject=${encodeURIComponent(
                  `Notify me when ${feature.name} is live`
                )}`}
              >
                Notify me when live
              </a>
            </div>
          </div>

          <div className="ws-soon-preview">
            <Preview />
            <div className="ws-soon-preview-veil" aria-hidden="true" />
            <div className="ws-soon-preview-tag mono">preview · concept</div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Concept previews — abstract, not literal screenshots ─────── */

const svgStyle = { width: '100%', height: '100%', display: 'block' };

function PreviewGeneric() {
  return (
    <svg viewBox="0 0 100 80" preserveAspectRatio="xMidYMid slice" style={svgStyle}>
      <rect width="100" height="80" fill="var(--bg-2)" />
      <g stroke="var(--line-strong)" strokeWidth=".2" fill="none">
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={i} x1="0" y1={i * 10} x2="100" y2={i * 10} />
        ))}
      </g>
    </svg>
  );
}

function PreviewSharpness() {
  return (
    <svg viewBox="0 0 200 160" preserveAspectRatio="xMidYMid slice" style={svgStyle}>
      <rect width="200" height="160" fill="var(--bg-2)" />
      {[0.15, 0.25, 0.4, 0.55, 0.7, 0.85, 0.95, 0.85, 0.7, 0.5, 0.3, 0.18, 0.1, 0.05].map(
        (h, i) => (
          <rect
            key={i}
            x={20 + i * 12}
            y={130 - h * 80}
            width="8"
            height={h * 80}
            fill={i > 8 ? 'var(--accent)' : 'var(--ink)'}
            opacity={i > 8 ? 1 : 0.18}
          />
        )
      )}
      <line x1="20" y1="130" x2="190" y2="130" stroke="var(--ink)" strokeWidth=".6" />
      <g transform="translate(20 18)">
        {[0, 1, 2, 3].map((i) => (
          <g key={i} transform={`translate(${i * 42} 0)`}>
            <rect
              width="36"
              height="20"
              fill={i === 0 ? 'var(--accent)' : 'var(--bg-2)'}
              stroke="var(--line-strong)"
              strokeWidth=".4"
            />
            <text x="2" y="14" fontSize="6" fontFamily="var(--mono)" fill={i === 0 ? '#fff' : 'var(--muted)'}>
              #{i + 1}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function PreviewSeg() {
  return (
    <svg viewBox="0 0 200 160" preserveAspectRatio="xMidYMid slice" style={svgStyle}>
      <rect width="200" height="160" fill="#cfc6b1" />
      <path d="M0 50 Q60 30 120 55 T200 55 L200 95 Q140 105 80 90 T0 95 Z" fill="oklch(60% 0.13 145)" opacity=".75" />
      <path d="M0 100 Q70 90 140 105 T200 100 L200 130 Q120 140 60 130 T0 135 Z" fill="oklch(55% 0.12 250)" opacity=".7" />
      <path d="M0 130 L200 130 L200 160 L0 160 Z" fill="#666057" opacity=".75" />
      <g fontFamily="var(--mono)" fontSize="6" fill="#fff">
        {[
          ['vegetation', 'oklch(60% 0.13 145)', 12],
          ['water', 'oklch(55% 0.12 250)', 26],
          ['built', '#666057', 40],
        ].map(([t, c, y]) => (
          <g key={t}>
            <rect x="12" y={y} width="8" height="6" fill={c} />
            <text x="24" y={y + 5}>{t}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function PreviewDetect() {
  return (
    <svg viewBox="0 0 200 160" preserveAspectRatio="xMidYMid slice" style={svgStyle}>
      <rect width="200" height="160" fill="#b8bdc1" />
      <rect x="0" y="100" width="200" height="60" fill="#888c92" opacity=".7" />
      <rect x="40" y="40" width="60" height="70" fill="#62686d" opacity=".7" />
      <rect x="120" y="60" width="70" height="50" fill="#525860" opacity=".7" />
      {[
        [50, 56, 42, 50, 'car · 0.94'],
        [130, 78, 50, 28, 'bus · 0.88'],
        [12, 90, 22, 38, 'person · 0.81'],
      ].map(([x, y, w, h, l]) => (
        <g key={l}>
          <rect x={x} y={y} width={w} height={h} fill="none" stroke="var(--accent)" strokeWidth="1" />
          <rect x={x} y={y - 8} width={l.length * 3.6 + 4} height="8" fill="var(--accent)" />
          <text x={x + 2} y={y - 2} fontSize="5.5" fontFamily="var(--mono)" fill="#fff">{l}</text>
        </g>
      ))}
    </svg>
  );
}

function PreviewTraffic() {
  return (
    <svg viewBox="0 0 200 160" preserveAspectRatio="xMidYMid slice" style={svgStyle}>
      <rect width="200" height="160" fill="#1c1d1a" />
      <defs>
        <radialGradient id="vl-hot" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="oklch(85% 0.18 60)" stopOpacity=".9" />
          <stop offset="60%" stopColor="oklch(60% 0.2 30)" stopOpacity=".5" />
          <stop offset="100%" stopColor="oklch(40% 0.2 280)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="80" cy="70" r="50" fill="url(#vl-hot)" />
      <circle cx="150" cy="100" r="34" fill="url(#vl-hot)" />
      <path d="M10 130 Q 60 90 110 110 T 190 80" stroke="oklch(85% 0.18 60)" strokeWidth="1.2" fill="none" strokeDasharray="3 3" opacity=".9" />
      <path d="M10 80 Q 70 50 130 70 T 190 40" stroke="oklch(75% 0.18 200)" strokeWidth="1.2" fill="none" strokeDasharray="3 3" opacity=".7" />
      <g fontFamily="var(--mono)" fontSize="6" fill="#fff" opacity=".8">
        <text x="14" y="20">DENSITY</text>
        <text x="14" y="148">— tracks · 24</text>
      </g>
    </svg>
  );
}

function PreviewMap() {
  const cells = [];
  for (let r = 0; r < 4; r++) for (let c = 0; c < 6; c++) cells.push({ r, c });
  return (
    <svg viewBox="0 0 200 160" preserveAspectRatio="xMidYMid slice" style={svgStyle}>
      <rect width="200" height="160" fill="#dcd5c4" />
      <g transform="translate(10 14)">
        {cells.map((c) => {
          const fill = ['#a89e85', '#bcad8c', '#cfc6b1', '#928871', '#a3a9af', '#777e86'][
            (c.r + c.c) % 6
          ];
          return (
            <rect
              key={`${c.r}-${c.c}`}
              x={c.c * 30}
              y={c.r * 32}
              width="29"
              height="31"
              fill={fill}
              stroke="rgba(0,0,0,.2)"
              strokeWidth=".4"
            />
          );
        })}
        <g stroke="var(--accent)" strokeWidth=".5" strokeDasharray="2 2" opacity=".8" fill="none">
          {[1, 2, 3, 4, 5].map((c) => (
            <line key={`v${c}`} x1={c * 30 - 0.5} y1="0" x2={c * 30 - 0.5} y2={4 * 32} />
          ))}
          {[1, 2, 3].map((r) => (
            <line key={`h${r}`} y1={r * 32 - 0.5} x1="0" y2={r * 32 - 0.5} x2={6 * 30} />
          ))}
        </g>
      </g>
      <text x="14" y="148" fontFamily="var(--mono)" fontSize="6" fill="#1a1a17" opacity=".7">
        ORTHOMOSAIC · 24 / 60 TILES
      </text>
    </svg>
  );
}

const PREVIEWS = {
  sharpness: PreviewSharpness,
  segment: PreviewSeg,
  detect: PreviewDetect,
  traffic: PreviewTraffic,
  mapper: PreviewMap,
};

export default ComingSoon;
